/* /biometric/punches — raw punch log with filters, CSV export and file import. */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { PunchQuery } from "../../data/types";
import { useData } from "../../data";
import { useAllEmployees, useClock, useDevices, usePunches } from "../../data/hooks";
import { Button, Card, Chip, EmptyState, Field, Input, Loaded, Modal, PageHeader, Select, errMsg, useToast } from "../../ui";
import { useI18n } from "../../i18n";
import { csvOf, downloadText } from "../../lib/format";
import { FileImport } from "./components/FileImport";
import { directionLabel, hm, verifyLabel } from "./components/labels";
import { Pager } from "./DeviceDetail";
import "./time.css";

type Who = "all" | "matched" | "unmatched";

export default function Punches() {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const p = useData();
  const { today } = useClock();
  /* null = follow the server clock (it may still be loading on first render). */
  const [dateSel, setDate] = useState<string | null>(null);
  const date = dateSel ?? today;
  const [device, setDevice] = useState("");
  const [who, setWho] = useState<Who>("all");
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [importDev, setImportDev] = useState("");
  const q: PunchQuery = { date: date || undefined, deviceId: device || undefined, unmatched: who === "all" ? undefined : who === "unmatched", page, pageSize: 25 };
  const punches = usePunches(q);
  const devs = useDevices();
  const emps = useAllEmployees();
  const claimed = (devs.data ?? []).filter((d) => d.claimed);
  const devName = (id: string) => (id === "ess" ? L("Mobile · geofence", "الجوال · نطاق جغرافي") : devs.data?.find((d) => d.id === id)?.name ?? id);
  const empName = (id: number | null) => { const e = id == null ? undefined : emps.data?.find((x) => x.id === id); return e ? i.name(e) : null; };
  const reset = <T,>(fn: (v: T) => void) => (v: T) => { fn(v); setPage(1); };

  const exportCsv = async () => {
    try {
      const all = await p.listPunches({ ...q, page: 1, pageSize: 1000 });
      const rows = all.items.map((x) => [x.time.replace("T", " "), devName(x.deviceId), x.deviceUserId, x.employeeId ?? "", empName(x.employeeId) ?? "", x.direction, x.verify, x.source]);
      downloadText(`rukn-punches-${date || "all"}.csv`, csvOf([["Time", "Device", "Device user", "Employee ID", "Employee", "Direction", "Verify", "Source"], ...rows]));
      toast(L(`${rows.length} punches exported`, `تم تصدير ${rows.length} بصمة`));
    } catch (e) { toast(errMsg(e), "bad"); }
  };

  return (
    <div data-testid="punches-page">
      <PageHeader back={{ to: "/biometric", label: L("Biometric devices", "أجهزة البصمة") }} title={L("Punch log", "سجل البصمات")}
        sub={L("Every punch received from devices, phones and files — before it becomes attendance.", "كل بصمة وصلت من الأجهزة والجوالات والملفات — قبل تحويلها إلى حضور.")}
        actions={<>
          <Button icon="upload" onClick={() => { setImportDev(claimed[0]?.id ?? ""); setImportOpen(true); }} data-testid="btn-import">{L("Import file", "استيراد ملف")}</Button>
          <Button icon="download" onClick={exportCsv} data-testid="btn-export">{t("Export")}</Button>
        </>} />
      <Card flush testId="punches-card">
        <div className="toolbar time-toolbar">
          <Input type="date" value={date} onChange={(e) => reset(setDate)(e.target.value)} aria-label={L("Date", "التاريخ")} data-testid="f-date" />
          <Select value={device} onChange={(e) => reset(setDevice)(e.target.value)} aria-label={L("Device", "الجهاز")} data-testid="f-device"
            options={[["", L("All devices", "كل الأجهزة")], ...claimed.map((d): [string, string] => [d.id, d.name]), ["ess", L("Mobile · geofence", "الجوال · نطاق جغرافي")]]} />
          <Select value={who} onChange={(e) => reset(setWho)(e.target.value as Who)} aria-label={t("Employee")} data-testid="f-who"
            options={[["all", L("Everyone", "الجميع")], ["matched", L("Linked to an employee", "مرتبطة بموظف")], ["unmatched", L("Unmatched only", "غير المرتبطة فقط")]]} />
          {(date !== today || device || who !== "all") && <Button variant="ghost sm" onClick={() => { setDate(today); setDevice(""); setWho("all"); setPage(1); }} data-testid="btn-clear">{L("Clear filters", "مسح الفلاتر")}</Button>}
        </div>
        <Loaded q={punches}>{(pg) => pg.items.length === 0 ? <EmptyState title={L("No punches for these filters", "لا توجد بصمات لهذه الفلاتر")} desc={L("Try another date or device.", "جرّب تاريخًا أو جهازًا آخر.")} /> : (
          <>
            <div className="tw"><table className="tbl cards compact" data-testid="punches-table">
              <thead><tr><th>{L("Time", "الوقت")}</th><th>{t("Employee")}</th><th className="hide-md">{L("Device user", "مستخدم الجهاز")}</th><th>{L("Device", "الجهاز")}</th><th>{L("Direction", "الاتجاه")}</th><th className="hide-md">{L("Verified by", "طريقة التحقق")}</th></tr></thead>
              <tbody>{pg.items.map((x) => (
                <tr key={x.id} data-testid={"punch-" + x.id}>
                  <td className="k3 num" data-l={L("Time", "الوقت")}>{x.time.slice(0, 10)} <b>{hm(x.time)}</b></td>
                  <td className="k1">{empName(x.employeeId) ?? <Link to="/biometric/unmatched"><Chip tone="warn">{L("Unmatched", "غير مرتبط")} · {x.deviceUserId}</Chip></Link>}</td>
                  <td className="hide-md num">{x.deviceUserId}</td>
                  <td className="x" data-l={L("Device", "الجهاز")}>{devName(x.deviceId)}</td>
                  <td className="k2">{directionLabel(x.direction, L)}</td>
                  <td className="hide-md sm muted">{verifyLabel(x.verify, L)}</td>
                </tr>
              ))}</tbody>
            </table></div>
            <Pager page={pg.page} size={pg.pageSize} total={pg.total} onPage={setPage} />
          </>
        )}</Loaded>
      </Card>

      {importOpen && (
        <Modal title={L("Import punches from a file", "استيراد البصمات من ملف")} onClose={() => setImportOpen(false)} testId="import-modal" foot={<Button onClick={() => setImportOpen(false)}>{t("Close")}</Button>}>
          <div className="stack">
            {claimed.length === 0 ? <EmptyState title={L("Add a device first", "أضف جهازًا أولًا")} desc={L("Imported punches are attached to a device (use “Any other device → File upload”).", "تُربط البصمات المستوردة بجهاز (استخدم «أي جهاز آخر ← رفع ملف»).")} /> : (
              <>
                <Field label={L("Device the file came from", "الجهاز الذي صدر منه الملف")} htmlFor="imp-dev">
                  <Select id="imp-dev" value={importDev} onChange={(e) => setImportDev(e.target.value)} options={claimed.map((d): [string, string] => [d.id, d.name])} data-testid="imp-device" />
                </Field>
                {importDev && <FileImport key={importDev} deviceId={importDev} />}
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
